/**
 * Storyboard Mode (doc 147 §6.6 E7 + PR Q5 scrubber).
 *
 * Walk a curated user journey one step at a time, with a timeline
 * scrubber + play/pause/step controls. Writable journeys (per the spec)
 * are deferred until auth lands; today the curated `STORYBOARDS` array
 * is the source of truth.
 */
import { useEffect, useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { STORYBOARDS } from '../state/storyboards.js';
import { EntityChip } from '../components/EntityChip.js';
import { STORYBOARD_PLAY_MS as PLAY_MS } from '../state/timings.js';

export interface StoryboardViewProps {
  manifest: AtlasManifest;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id (from `#storyboard/focus/<id>`) — surfaces a scope banner. */
  focus?: string;
}

export function StoryboardView({
  manifest,
  onNavigateEntity,
  focus,
}: StoryboardViewProps): React.JSX.Element {
  const [storyboardId, setStoryboardId] = useState<string>(STORYBOARDS[0]!.id);
  const story = STORYBOARDS.find((s) => s.id === storyboardId);
  const [stepIdx, setStepIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Reset on journey change.
  useEffect(() => {
    setStepIdx(0);
    setPlaying(false);
  }, [storyboardId]);

  // Auto-play.
  useEffect(() => {
    if (!playing || !story) return;
    const t = window.setInterval(() => {
      setStepIdx((i) => {
        if (i >= story.steps.length - 1) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, PLAY_MS);
    return () => window.clearInterval(t);
  }, [playing, story]);

  return (
    <div className="storyboard-view">
      <header className="storyboard-head">
        <h2>Storyboard Mode</h2>
        {focus && (
          <p className="scope-banner" role="status">
            Focused on <strong>{focus}</strong> — pick a journey that
            includes this entity.
          </p>
        )}
        <label className="storyboard-picker">
          Journey
          <select
            aria-label="Journey"
            value={storyboardId}
            onChange={(e) => setStoryboardId(e.target.value)}
          >
            {STORYBOARDS.map((s) => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>
        </label>
      </header>

      {story && (
        <>
          <p className="subtitle">
            Persona: <code>{story.persona}</code> · {story.steps.length} step{story.steps.length === 1 ? '' : 's'}
          </p>

          <section className="storyboard-scrubber" aria-label="Step scrubber">
            <header className="storyboard-scrubber-controls">
              <span className="storyboard-scrubber-label">
                STEP {stepIdx + 1} / {story.steps.length}
              </span>
              <div className="storyboard-scrubber-buttons">
                <button
                  type="button"
                  className="btn btn-ghost"
                  aria-label="Step back"
                  onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
                  disabled={stepIdx <= 0}
                >
                  ⏮
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  aria-label={playing ? 'Pause' : 'Play'}
                  onClick={() => {
                    if (playing) setPlaying(false);
                    else {
                      setStepIdx(0);
                      setPlaying(true);
                    }
                  }}
                >
                  {playing ? '⏸ Pause' : '⏯ Play'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  aria-label="Step forward"
                  onClick={() => setStepIdx((s) => Math.min(story.steps.length - 1, s + 1))}
                  disabled={stepIdx >= story.steps.length - 1}
                >
                  ⏭
                </button>
              </div>
            </header>
            <div className="storyboard-track">
              {story.steps.map((s, i) => {
                const completed = i < stepIdx;
                const active = i === stepIdx;
                return (
                  <button
                    key={i}
                    type="button"
                    className={`storyboard-tick${active ? ' is-active' : ''}${completed ? ' is-completed' : ''}`}
                    onClick={() => setStepIdx(i)}
                    aria-label={`Step ${i + 1}: ${s.title}`}
                  >
                    <span className="storyboard-tick-num">{i + 1}</span>
                    <span className="storyboard-tick-title">{s.title}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <article className="storyboard-step-card" aria-label={`Step ${stepIdx + 1}`}>
            <h3 className="storyboard-step-card-title">
              Step {stepIdx + 1} · {story.steps[stepIdx]?.title}
            </h3>
            <p className="storyboard-step-card-desc">{story.steps[stepIdx]?.description}</p>
            <dl>
              {story.steps[stepIdx]?.surfaces.length ? (
                <div>
                  <dt>Surfaces</dt>
                  <dd>
                    {story.steps[stepIdx]!.surfaces.map((s) => (
                      <span key={s} className="mono pill">{s}</span>
                    ))}
                  </dd>
                </div>
              ) : null}
              {story.steps[stepIdx]?.entities.length ? (
                <div>
                  <dt>Entities</dt>
                  <dd>
                    {story.steps[stepIdx]!.entities.map((e) => (
                      <EntityChip key={e} entityId={e} manifest={manifest} onClick={onNavigateEntity} />
                    ))}
                  </dd>
                </div>
              ) : null}
              {story.steps[stepIdx]?.events.length ? (
                <div>
                  <dt>Events</dt>
                  <dd>
                    {story.steps[stepIdx]!.events.map((ev) => (
                      <span key={ev} className="chip info">{ev}</span>
                    ))}
                  </dd>
                </div>
              ) : null}
            </dl>
          </article>
        </>
      )}
    </div>
  );
}
